import React, { useState, useEffect } from 'react';
import './QuizGame.css';

const OnlinePlayersLobby = ({ userRole, userName, userClass, userEmail, userId, onStartGame, onExit }) => {
  const [onlinePlayers, setOnlinePlayers] = useState([]);
  const [gameRequests, setGameRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch game requests
  useEffect(() => {
    const fetchGameRequests = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/game-requests/${userName}`);
        if (response.ok) {
          const requests = await response.json();
          setGameRequests(requests);
        }
      } catch (error) {
        console.error('Error fetching game requests:', error);
      }
    };

    if (userName) {
      fetchGameRequests();
      const interval = setInterval(fetchGameRequests, 3000);
      return () => clearInterval(interval);
    }
  }, [userName]);

  // Real online status system with auto-refresh
  useEffect(() => {
    const updateOnlineStatus = async () => {
      try {
        const uniqueId = userId || userName + '_' + userRole;
        const displayName = userName || userEmail || `User_${uniqueId}`;
        await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users/online-status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            userId: uniqueId, 
            name: displayName, 
            email: userEmail,
            role: userRole, 
            class: userClass 
          })
        });
      } catch (error) {
        console.error('Failed to update online status:', error);
      }
    };

    const fetchOnlinePlayers = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/games/online-players`);
        if (response.ok) {
          const players = await response.json();
          const otherPlayers = players.filter(player => player.name !== userName);
          setOnlinePlayers(otherPlayers);
        }
      } catch (error) {
        console.error('Error fetching online players:', error);
        setOnlinePlayers([]);
      } finally {
        setLoading(false);
      }
    };

    // Update status immediately and every 10 seconds
    updateOnlineStatus();
    fetchOnlinePlayers();
    
    const statusInterval = setInterval(updateOnlineStatus, 10000);
    const fetchInterval = setInterval(fetchOnlinePlayers, 2000); // Faster refresh for auto-updates
    
    return () => {
      clearInterval(statusInterval);
      clearInterval(fetchInterval);
    };
  }, [userName, userRole, userClass, userEmail, userId]);

  const sendGameRequest = async (targetPlayer) => {
    try {
      setSentRequests(prev => [...prev, targetPlayer.name]);
      
      await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/game-requests/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromPlayer: userName,
          fromRole: userRole,
          fromClass: userClass,
          toPlayer: targetPlayer.name,
          toRole: targetPlayer.role,
          toClass: targetPlayer.class
        })
      });
      
      alert(`Challenge sent to ${targetPlayer.name}!`);
    } catch (error) {
      console.error('Error sending challenge:', error);
      setSentRequests(prev => prev.filter(name => name !== targetPlayer.name));
    }
  };

  const acceptGameRequest = async (request) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/game-requests/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestId: request._id,
          player1: request.fromPlayer,
          player2: userName
        })
      });
      
      if (response.ok) {
        const { gameSession } = await response.json();
        onStartGame({
          mode: 'live-challenge',
          players: gameSession.players,
          gameSession
        });
      }
    } catch (error) {
      console.error('Error accepting game request:', error);
    }
  };

  const rejectGameRequest = async (requestId) => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/game-requests/${requestId}`, {
        method: 'DELETE'
      });
      setGameRequests(prev => prev.filter(req => req._id !== requestId));
    } catch (error) {
      console.error('Error rejecting game request:', error);
    }
  };

  const canChallenge = (player) => {
    const isTeacher = ['teacher', 'admin', 'admin1', 'teacher_admin_level1'].includes(userRole);
    const isPlayerTeacher = ['teacher', 'admin', 'admin1', 'teacher_admin_level1'].includes(player.role);
    
    if (isTeacher) return true;
    if (!isTeacher && isPlayerTeacher) return true;
    return false;
  };

  return (
    <div className="quiz-game">
      <div className="prize-ladder-container" style={{ maxWidth: '600px' }}>
        <h2>🌐 Online Players</h2>
        

        
        <div style={{ marginBottom: '25px' }}>
          <h3 style={{ color: '#000' }}>🎮 Game Requests ({gameRequests.length})</h3>
          {gameRequests.length === 0 ? (
            <div style={{
              background: 'rgba(107, 70, 193, 0.1)',
              padding: '15px',
              borderRadius: '10px',
              textAlign: 'center',
              color: '#666'
            }}>
              No pending game requests
            </div>
          ) : (
            gameRequests.map((request, index) => (
              <div key={index} style={{
                background: 'rgba(16, 185, 129, 0.1)',
                padding: '15px',
                borderRadius: '10px',
                margin: '10px 0',
                border: '2px solid #10b981'
              }}>
                <div style={{ marginBottom: '10px', color: '#000', fontWeight: 'bold' }}>
                  🎯 {request.fromPlayer} wants to challenge you!
                </div>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
                  {request.fromRole} from Class {request.fromClass}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button 
                    onClick={() => acceptGameRequest(request)}
                    className="continue-btn"
                    style={{ flex: 1, padding: '12px', fontSize: '16px', fontWeight: 'bold' }}
                  >
                    ✅ Accept Challenge
                  </button>
                  <button 
                    onClick={() => rejectGameRequest(request._id)}
                    className="quit-btn"
                    style={{ flex: 1, padding: '12px', fontSize: '16px' }}
                  >
                    ❌ Decline
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div style={{ marginBottom: '25px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h3 style={{ color: '#000' }}>👥 Players Online ({onlinePlayers.length})</h3>
          </div>
          
          {loading ? (
            <div style={{
              background: 'rgba(107, 70, 193, 0.1)',
              padding: '20px',
              borderRadius: '10px',
              textAlign: 'center',
              color: '#666'
            }}>
              <div>🔄 Loading online players...</div>
            </div>
          ) : onlinePlayers.length === 0 ? (
            <div style={{
              background: 'rgba(107, 70, 193, 0.1)',
              padding: '20px',
              borderRadius: '10px',
              textAlign: 'center',
              color: '#666'
            }}>
              <div>No other players online right now. Try again later!</div>
              <div style={{ fontSize: '12px', marginTop: '10px', color: '#999' }}>
                Current user: {userName || 'No Name'} ({userRole})
              </div>
            </div>
          ) : (
            <div>
              {onlinePlayers.filter(p => ['teacher', 'admin', 'admin1', 'teacher_admin_level1'].includes(p.role)).length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#000', marginBottom: '10px' }}>👨🏫 Teachers Online</h4>
                  {onlinePlayers
                    .filter(p => ['teacher', 'admin', 'admin1', 'teacher_admin_level1'].includes(p.role))
                    .map((player, index) => (
                      <div key={index} style={{
                        background: 'rgba(107, 70, 193, 0.1)',
                        padding: '15px',
                        borderRadius: '10px',
                        margin: '8px 0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <div style={{ fontWeight: 'bold', color: '#000' }}>{player.name.replace(/&#39;/g, "'")}</div>
                          <div style={{ fontSize: '14px', color: '#666' }}>
                            {player.role} • Class {player.class}
                          </div>
                          <div style={{ fontSize: '12px', color: '#10b981' }}>
                            🟢 Online
                          </div>
                        </div>
                        
                        {canChallenge(player) && !sentRequests.includes(player.name) && (
                          <button 
                            onClick={() => sendGameRequest(player)}
                            className="continue-btn"
                            style={{ padding: '8px 16px', fontSize: '14px' }}
                          >
                            🎯 Challenge
                          </button>
                        )}
                        
                        {sentRequests.includes(player.name) && (
                          <div style={{ 
                            background: '#fbbf24', 
                            color: 'white', 
                            padding: '8px 16px', 
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}>
                            ⏳ Request Sent
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}

              {['teacher', 'admin', 'admin1', 'teacher_admin_level1'].includes(userRole) && 
               onlinePlayers.filter(p => p.role === 'student').length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#000', marginBottom: '10px' }}>👨🎓 Students Online</h4>
                  {onlinePlayers
                    .filter(p => p.role === 'student')
                    .map((player, index) => (
                      <div key={index} style={{
                        background: 'rgba(16, 185, 129, 0.1)',
                        padding: '15px',
                        borderRadius: '10px',
                        margin: '8px 0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <div style={{ fontWeight: 'bold', color: '#000' }}>{player.name.replace(/&#39;/g, "'")}</div>
                          <div style={{ fontSize: '14px', color: '#666' }}>
                            Student • Class {player.class}
                          </div>
                          <div style={{ fontSize: '12px', color: '#10b981' }}>
                            🟢 Online
                          </div>
                        </div>
                        
                        {!sentRequests.includes(player.name) && (
                          <button 
                            onClick={() => sendGameRequest(player)}
                            className="continue-btn"
                            style={{ padding: '8px 16px', fontSize: '14px' }}
                          >
                            🎯 Challenge
                          </button>
                        )}
                        
                        {sentRequests.includes(player.name) && (
                          <div style={{ 
                            background: '#fbbf24', 
                            color: 'white', 
                            padding: '8px 16px', 
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}>
                            ⏳ Request Sent
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        <div style={{ fontSize: '12px', color: '#999', textAlign: 'center', marginTop: '10px' }}>
          🟢 Live • {onlinePlayers.length} players online
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onExit} className="quit-btn" style={{ flex: 1 }}>
            Back to Games
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnlinePlayersLobby;